/**
 * ---------------------------------------------------------------------------------------------------------------
 * BUNDLE BUILDER
 * ---------------------------------------------------------------------------------------------------------------
 *
 * Drives the "Build Your Own" tab of the PDP bundle drawer: tracks the chosen variants, works out which volume
 * tier the shopper has reached, keeps the ladder / footer in sync, and adds the whole bundle in one request.
 *
 * The add-to-cart flow deliberately mirrors the theme's own ProductForm (assets/theme.js.liquid) so the cart
 * drawer and the "variant added" popover keep working: same bundled sections, same `variant:add` / `cart:change`
 * events.
 *
 * Discounting
 * -----------
 * Pricing is applied by the bundle-tier Discount Function (custom app), which reads the same bundle.tiers shop
 * metafield this UI renders from and groups cart lines by their _bundle property. Nothing here applies a discount;
 * the function does it at cart/checkout evaluation time.
 *
 * Goal mode (config.mode === 'goal')
 * ----------------------------------
 * The same builder selling something other than a saving — completing a collection, stocking up. It ships with no
 * tiers at all, so there is no ladder to render, nothing unlocks, and the totals stay at full price. Its line
 * items deliberately carry a property the discount function does NOT look at: stamping _bundle here would price a
 * bundle nobody promised a discount on.
 */

class BundleBuilder extends HTMLElement {
  connectedCallback() {
    // The drawer relocates itself to document.body when it opens, which disconnects and RECONNECTS this element.
    // Re-initialising on that reconnect would wipe a selection seeded from the cart (the checkout-intercept flow)
    // and its absorb map — so state is built exactly once. The click listener is re-attached every time because
    // disconnectedCallback removes it; addEventListener with the same function reference never duplicates.
    this.addEventListener('click', this._onClick);

    if (this._initialized) {
      return;
    }
    this._initialized = true;

    this.config = this._readConfig();
    this.isGoal = this.config.mode === 'goal';
    this.tiers = (this.config.tiers || []).slice().sort((a, b) => a.quantity - b.quantity);
    // Goal mode states its minimum outright, because it has no ladder to derive one from. Discount mode still
    // takes it from the lowest tier.
    this.minQuantity = this.config.minQuantity > 0 ? this.config.minQuantity : this.tiers[0]?.quantity || 1;

    /** @type {Map<string, {quantity: number, price: number, title: string, image: string, max: number}>} */
    this.selection = new Map();

    this.cards = Array.from(this.querySelectorAll('[data-bb-card]'));
    this.ladderFill = this.querySelector('[data-bb-ladder-fill]');
    this.ladderMessage = this.querySelector('[data-bb-ladder-message]');
    this.ladderSteps = Array.from(this.querySelectorAll('[data-bb-ladder-step]'));
    this.tray = this.querySelector('[data-bb-tray]');
    this.totalValue = this.querySelector('[data-bb-total]');
    this.savingValue = this.querySelector('[data-bb-saving]');
    this.submitButton = this.querySelector('[data-bb-submit]');
    this.errorElement = this.querySelector('[data-bb-error]');
    this.preorderNote = this.querySelector('[data-bb-preorder-note]');

    this._seedInitialSelection();
    this._render();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
  }

  /* -------------------------------------------------------------------------------------------------------------
     Setup
  ------------------------------------------------------------------------------------------------------------- */

  _readConfig() {
    const script = this.querySelector('[data-bb-config]');

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error('[bundle-builder] invalid config', error);
      return { tiers: [] };
    }
  }

  /**
   * Seeds the builder from cart lines (the checkout-intercept upsell). Only lines whose variant has a card in the
   * grid can be seeded. Seeded lines are remembered so that adding the bundle ABSORBS them — the loose cart line
   * is removed once its variant ships inside the bundle, preventing duplicates. A line whose variant the shopper
   * removes from the builder before submitting is left in the cart untouched.
   *
   * @param {Array<{variantId: string, quantity: number, lineKey: string}>} entries
   * @returns {number} how many entries were seeded
   */
  seedFromCart(entries) {
    this._absorb = {};
    let seeded = 0;

    for (const entry of entries || []) {
      if (!this._cardFor(entry.variantId)) {
        continue;
      }

      this._setQuantity(entry.variantId, entry.quantity);

      if (this.selection.has(entry.variantId)) {
        this._absorb[entry.variantId] = entry.lineKey;
        seeded += 1;
      }
    }

    this._render();
    this._promoteCuratedFor(entries);
    return seeded;
  }

  /** Pre-curated bundles that CONTAIN a seeded product float to the top of the other tab. */
  _promoteCuratedFor(entries) {
    const productIds = (entries || []).map((entry) => String(entry.productId)).filter(Boolean);
    const grid = this.closest('x-drawer')?.querySelector('bundle-curated');

    if (!productIds.length || !grid) {
      return;
    }

    const matches = Array.from(grid.querySelectorAll('[data-contains]')).filter((card) => {
      const ids = (card.dataset.contains || '').split(',');
      return productIds.some((id) => ids.includes(id));
    });

    matches.reverse().forEach((card) => grid.prepend(card));
  }

  /**
   * The product the shopper is already looking at is pre-loaded into the bundle (but can be removed like any other).
   */
  _seedInitialSelection() {
    const seedCard = this.querySelector('[data-bb-card][data-seed="true"]');

    if (seedCard) {
      this._setQuantity(seedCard.dataset.variantId, 1);
    }
  }

  _cardFor(variantId) {
    return this.cards.find((card) => card.dataset.variantId === variantId);
  }

  /* -------------------------------------------------------------------------------------------------------------
     Interaction
  ------------------------------------------------------------------------------------------------------------- */

  _onClick = (event) => {
    const trigger = event.target.closest('[data-bb-action]');

    if (!trigger || !this.contains(trigger)) {
      return;
    }

    event.preventDefault();

    const { bbAction: action, variantId } = trigger.dataset;
    const current = this.selection.get(variantId)?.quantity || 0;

    switch (action) {
      case 'add':
      case 'increase':
        this._setQuantity(variantId, current + 1);
        break;

      case 'decrease':
        this._setQuantity(variantId, current - 1);
        break;

      case 'remove':
        this._setQuantity(variantId, 0);
        break;

      case 'submit':
        this._submit();
        return;
    }

    this._render();
  };

  _setQuantity(variantId, quantity) {
    const card = this._cardFor(variantId);

    if (!card) {
      return;
    }

    const max = parseInt(card.dataset.max, 10) || Infinity;
    const next = Math.max(0, Math.min(quantity, max));

    if (next === 0) {
      this.selection.delete(variantId);
      return;
    }

    this.selection.set(variantId, {
      quantity: next,
      price: parseInt(card.dataset.price, 10) || 0,
      title: card.dataset.title || '',
      image: card.dataset.image || '',
      preorder: card.dataset.preorder === 'true',
      max
    });
  }

  /* -------------------------------------------------------------------------------------------------------------
     Pricing
  ------------------------------------------------------------------------------------------------------------- */

  get totalQuantity() {
    let total = 0;
    this.selection.forEach((item) => (total += item.quantity));
    return total;
  }

  get subtotal() {
    let total = 0;
    this.selection.forEach((item) => (total += item.price * item.quantity));
    return total;
  }

  /**
   * The property stamped on every line of the bundle. Goal bundles are never discounted, so they must not carry
   * the property the discount function groups on — bundle-builder-byo.liquid already swaps it, and this is the
   * belt to that pair of braces, since the value arrives from merchant-editable config.
   */
  get bundleProperty() {
    const property = this.config.bundleProperty || '_bundle';
    return this.isGoal && property === '_bundle' ? '_kit' : property;
  }

  /** The highest tier the current quantity has unlocked, or null. */
  get reachedTier() {
    const quantity = this.totalQuantity;
    let reached = null;

    this.tiers.forEach((tier) => {
      if (quantity >= tier.quantity) {
        reached = tier;
      }
    });

    return reached;
  }

  /** The next tier still to be unlocked, or null when the top rung is reached. */
  get nextTier() {
    const quantity = this.totalQuantity;
    return this.tiers.find((tier) => quantity < tier.quantity) || null;
  }

  /* -------------------------------------------------------------------------------------------------------------
     Rendering
  ------------------------------------------------------------------------------------------------------------- */

  _render() {
    const quantity = this.totalQuantity;
    const subtotal = this.subtotal;
    const reached = this.reachedTier;
    // Milestones carry no discount key at all, so this must coerce rather than trust the rung.
    const discount = (reached && reached.discount) || 0;
    const total = Math.round(subtotal * (1 - discount / 100));

    this._renderCards();
    this._renderLadder(quantity, discount);
    this._renderTray();
    this._renderTotals(subtotal, total, discount);
    this._renderSubmit(quantity, total);
    this._renderPreorderNote();
    this._setError(null);
  }

  _renderCards() {
    this.cards.forEach((card) => {
      const item = this.selection.get(card.dataset.variantId);
      const quantity = item?.quantity || 0;

      card.dataset.selected = quantity > 0 ? 'true' : 'false';

      const output = card.querySelector('[data-bb-quantity]');
      if (output) {
        output.textContent = quantity;
      }

      const count = card.querySelector('[data-bb-count]');
      if (count) {
        count.textContent = quantity;
      }

      const increase = card.querySelector('[data-bb-action="increase"]');
      if (increase) {
        increase.disabled = quantity >= (parseInt(card.dataset.max, 10) || Infinity);
      }
    });
  }

  _renderLadder(quantity, discount) {
    const topTier = this.tiers[this.tiers.length - 1];

    if (this.ladderFill && topTier) {
      this.ladderFill.style.width = `${Math.min(100, (quantity / topTier.quantity) * 100)}%`;
    }

    this.ladderSteps.forEach((step) => {
      step.dataset.reached = quantity >= parseInt(step.dataset.bbLadderStep, 10) ? 'true' : 'false';
    });

    if (!this.ladderMessage) {
      return;
    }

    const next = this.nextTier;
    const labels = this.config.labels || {};

    if (next) {
      const remaining = next.quantity - quantity;

      this.ladderMessage.innerHTML = this._interpolate(remaining === 1 ? labels.ladderNextOne : labels.ladderNext, {
        count: remaining,
        discount: next.discount
      });
    } else if (discount > 0) {
      this.ladderMessage.innerHTML = this._interpolate(labels.ladderMax, { discount });
    } else {
      this.ladderMessage.innerHTML = '';
    }
  }

  _renderTray() {
    if (!this.tray) {
      return;
    }

    const labels = this.config.labels || {};
    const slots = [];

    this.selection.forEach((item, variantId) => {
      for (let index = 0; index < item.quantity; index++) {
        slots.push(`
          <div class="bb-slot">
            ${item.image ? `<img src="${item.image}" alt="" loading="lazy" width="48" height="48">` : ''}
            <button type="button"
                    class="bb-slot__remove"
                    data-bb-action="decrease"
                    data-variant-id="${variantId}"
                    aria-label="${this._escape(this._interpolate(labels.removeItem, { title: item.title }))}">&times;</button>
          </div>
        `);
      }
    });

    // Pad out to the first tier so the shopper can see how many slots are still to fill.
    for (let index = slots.length; index < this.minQuantity; index++) {
      slots.push('<div class="bb-slot bb-slot--empty" aria-hidden="true">+</div>');
    }

    this.tray.innerHTML = slots.join('');
  }

  _renderTotals(subtotal, total, discount) {
    const labels = this.config.labels || {};

    if (this.totalValue) {
      this.totalValue.innerHTML =
        discount > 0
          ? `${this._formatMoney(total)}<s>${this._formatMoney(subtotal)}</s>`
          : this._formatMoney(subtotal);
    }

    if (this.savingValue) {
      // Goal mode has no saving to report, so the slot carries a plain count of what's in the kit instead.
      if (this.isGoal) {
        const quantity = this.totalQuantity;
        this.savingValue.textContent = quantity > 0 ? this._interpolate(labels.goalSummary, { count: quantity }) : '';
      } else {
        this.savingValue.textContent =
          discount > 0
            ? this._interpolate(labels.saving, { amount: this._formatMoney(subtotal - total), discount })
            : '';
      }
    }
  }

  _renderSubmit(quantity, total) {
    if (!this.submitButton) {
      return;
    }

    const labels = this.config.labels || {};
    const label = this.submitButton.querySelector('[data-bb-submit-label]') || this.submitButton;

    if (quantity < this.minQuantity) {
      this.submitButton.disabled = true;
      label.textContent = this._interpolate(labels.submitIncomplete, { count: this.minQuantity - quantity });
    } else {
      this.submitButton.disabled = false;
      label.textContent = this._interpolate(labels.submit, {
        count: quantity,
        total: this._formatMoney(total)
      });
    }
  }

  /** Shown while the bundle contains at least one preorder item, so nobody is surprised at dispatch time. */
  _renderPreorderNote() {
    if (!this.preorderNote) {
      return;
    }

    let hasPreorder = false;
    this.selection.forEach((item) => {
      if (item.preorder) {
        hasPreorder = true;
      }
    });

    this.preorderNote.hidden = !hasPreorder;
  }

  _setError(message) {
    if (!this.errorElement) {
      return;
    }

    this.errorElement.textContent = message || '';
    this.errorElement.dataset.visible = message ? 'true' : 'false';
  }

  /* -------------------------------------------------------------------------------------------------------------
     Add to cart
  ------------------------------------------------------------------------------------------------------------- */

  async _submit() {
    if (this.totalQuantity < this.minQuantity || this.dataset.busy === 'true') {
      return;
    }

    this.dataset.busy = 'true';
    this.submitButton?.setAttribute('aria-busy', 'true');

    const reached = this.reachedTier;
    // The set prefix tells the discount function which named ladder this bundle was sold under; no prefix means
    // the default ladder. Both sides read the same bundle.tiers metafield, so the numbers cannot diverge.
    const setPrefix = this.config.tierSet ? `${this.config.tierSet}:` : '';
    const bundleId = `${setPrefix}${this.config.bundleKey || 'bundle'}-${Date.now()}`;
    const items = [];

    this.selection.forEach((item, variantId) => {
      items.push({
        id: parseInt(variantId, 10),
        quantity: item.quantity,
        properties: {
          [this.bundleProperty]: bundleId
        }
      });
    });

    try {
      await this._addItems(items, reached);
    } catch (error) {
      this._setError(error.message || (this.config.labels || {}).genericError);
    } finally {
      this.dataset.busy = 'false';
      this.submitButton?.removeAttribute('aria-busy');
    }
  }

  /**
   * Posts the line items, then either hands off to the discount URL or refreshes the cart in place.
   */
  async _addItems(items, tier) {
    const root = window.Shopify.routes.root;

    // Ask the rest of the theme which sections it wants re-rendered, exactly as ProductForm does.
    const sectionsToBundle = ['variant-added'];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest' // needed for Shopify to check inventory
      },
      // sections_url is required: without it Shopify returns no rendered sections at all, and CartDrawer's
      // _onCartChanged does cart["sections"][sectionId] unguarded — it would throw and leave the drawer stale.
      body: JSON.stringify({
        items,
        sections: sectionsToBundle.join(','),
        sections_url: window.location.pathname
      })
    });

    const responseJson = await response.json();

    if (!response.ok) {
      throw new Error(responseJson.description || responseJson.message);
    }

    await this._absorbSeededLines();

    if (window.themeVariables.settings.cartType === 'page' || window.themeVariables.settings.pageType === 'cart') {
      window.location.href = `${root}cart`;
      return;
    }

    const cartContent = await (await fetch(`${root}cart.js`)).json();
    cartContent['sections'] = responseJson['sections'];

    this.closest('x-drawer')?.hide();

    this.dispatchEvent(
      new CustomEvent('variant:add', {
        bubbles: true,
        detail: { items: responseJson['items'] || [responseJson], cart: cartContent }
      })
    );

    this._notifyCartChanged(cartContent);
  }

  /**
   * Removes the loose cart lines that were seeded into this bundle and are still part of the final selection —
   * their variants now live inside the bundle at the discounted price, so leaving the originals would duplicate
   * them. Seeded variants the shopper removed from the builder stay in the cart untouched.
   */
  async _absorbSeededLines() {
    const absorb = this._absorb || {};
    const updates = {};

    for (const [variantId, lineKey] of Object.entries(absorb)) {
      if (this.selection.has(variantId) && lineKey) {
        updates[lineKey] = 0;
      }
    }

    this._absorb = {};

    if (Object.keys(updates).length === 0) {
      return;
    }

    try {
      await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates })
      });
    } catch (error) {
      // Failing to absorb must not break the add itself; worst case the shopper sees the duplicate loose line in
      // the cart and removes it. Log for diagnosis.
      console.error('[bundle-builder] could not absorb seeded cart lines', error);
    }
  }

  /**
   * Belt and braces: cart:change assumes the rendered sections are present. If section rendering returned nothing
   * (a redirecting or otherwise unrenderable current URL), fall back to cart:refresh, which makes the cart drawer
   * re-fetch its own markup instead of throwing on an undefined section.
   */
  _notifyCartChanged(cartContent) {
    const sections = cartContent['sections'];

    if (sections && Object.keys(sections).length > 0) {
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'variant:add', cart: cartContent }
        })
      );
    } else {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }

    // This store's cart UI is the Unicorn app (the theme's own cart drawer is disabled), and it doesn't listen to
    // the theme's events — refresh and open it through its own API so the shopper actually SEES their new bundle.
    try {
      const unicorn = window.Unicorn;

      if (unicorn && typeof unicorn.openCart === 'function') {
        Promise.resolve(typeof unicorn.refreshCart === 'function' ? unicorn.refreshCart() : null).finally(() =>
          unicorn.openCart()
        );
      }
    } catch {
      // the events above remain the fallback
    }
  }

  /* -------------------------------------------------------------------------------------------------------------
     Helpers
  ------------------------------------------------------------------------------------------------------------- */

  /**
   * Copy placeholders use [count] rather than {{ count }} on purpose: Shopify parses {{ ... }} inside a JSON
   * template's setting values as a dynamic source binding and rejects the template on upload.
   */
  _interpolate(template, replacements) {
    if (!template) {
      return '';
    }

    return Object.keys(replacements).reduce(
      (result, key) => result.replaceAll(`[${key}]`, replacements[key]),
      template
    );
  }

  _escape(value) {
    return String(value).replace(/[&<>"']/g, (character) => {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character];
    });
  }

  /**
   * Minimal money formatter covering the money_format placeholders Shopify actually emits.
   */
  _formatMoney(cents) {
    const format = this.config.moneyFormat || '${{amount}}';
    const placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/);

    if (!placeholder) {
      return format;
    }

    const withDelimiters = (precision, thousands, decimal) => {
      const value = (cents / 100.0).toFixed(precision);
      const parts = value.split('.');
      const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? `${whole}${decimal}${parts[1]}` : whole;
    };

    let value;

    switch (placeholder[1]) {
      case 'amount':
        value = withDelimiters(2, ',', '.');
        break;
      case 'amount_no_decimals':
        value = withDelimiters(0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(2, "'", '.');
        break;
      default:
        value = withDelimiters(2, ',', '.');
    }

    return format.replace(placeholder[0], value);
  }
}

if (!window.customElements.get('bundle-builder')) {
  window.customElements.define('bundle-builder', BundleBuilder);
}

/**
 * ---------------------------------------------------------------------------------------------------------------
 * PRE-CURATED BUNDLES
 * ---------------------------------------------------------------------------------------------------------------
 *
 * Each pre-curated bundle is a real, already-priced product, so adding one is a plain single-variant add — no tier
 * maths and no discount code involved.
 */
class BundleCurated extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this._onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
  }

  _onClick = async (event) => {
    const button = event.target.closest('[data-bb-curated-add]');

    if (!button || button.disabled) {
      return;
    }

    event.preventDefault();

    const root = window.Shopify.routes.root;
    const sectionsToBundle = ['variant-added'];

    button.setAttribute('aria-busy', 'true');
    button.disabled = true;

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        // See BundleBuilder._addItems — sections_url is required or CartDrawer throws on an undefined section.
        body: JSON.stringify({
          items: [{ id: parseInt(button.dataset.variantId, 10), quantity: 1 }],
          sections: sectionsToBundle.join(','),
          sections_url: window.location.pathname
        })
      });

      const responseJson = await response.json();

      if (!response.ok) {
        throw new Error(responseJson.description || responseJson.message);
      }

      if (window.themeVariables.settings.cartType === 'page' || window.themeVariables.settings.pageType === 'cart') {
        window.location.href = `${root}cart`;
        return;
      }

      const cartContent = await (await fetch(`${root}cart.js`)).json();
      cartContent['sections'] = responseJson['sections'];

      this.closest('x-drawer')?.hide();

      this.dispatchEvent(
        new CustomEvent('variant:add', {
          bubbles: true,
          detail: { items: responseJson['items'] || [responseJson], cart: cartContent }
        })
      );

      if (cartContent['sections'] && Object.keys(cartContent['sections']).length > 0) {
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', {
            bubbles: true,
            detail: { baseEvent: 'variant:add', cart: cartContent }
          })
        );
      } else {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
    } catch (error) {
      console.error('[bundle-curated]', error);
    } finally {
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }
  };
}

if (!window.customElements.get('bundle-curated')) {
  window.customElements.define('bundle-curated', BundleCurated);
}
